import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { dataUser } from '../Helpers/util';
import { toast } from 'react-toastify';
import { MultiSelect } from 'react-multi-select-component';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import axiosInstance from '../../utils/axiosInstance';
import SwSelect from '../Common/SwReactSelect/SwReactSelect';
import SwMultiSelect from '../Common/SwMultiSelect/SwMultiSelect';
import SwButton from '../Common/SwButtons/SwButtons';
import AntDDatePicker from '../Common/AntDDateTimePicker/AntDDatePicker';
import SwOffCanvas from '../Common/SwOffCanvas/SwOffCanvas';

const userDataLocal = dataUser();
const UserObj = JSON.parse(userDataLocal);

const AccessLogsExport = ({ show, onClose }) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
  });
  const [errors, setErrors] = useState({});
  const [loadingExport, setLoadingExport] = useState(false);
  const [isCustomModalVisible, setIsCustomModalVisible] = useState(false);
  const [customDate, setCustomDate] = useState({
    startDate: null,
    endDate: null,
    selectedLocations: [],
    deviceType: ['All'],
  });
  const [customErrors, setCustomErrors] = useState({});
  const [deviceType, setDeviceType] = useState(['All']);
  const [allDeviceIds, setAllDeviceIds] = useState([]);
  const [fetchingDevices, setFetchingDevices] = useState(false);

  const today = dayjs();
  const threeMonthsAgoStart = today.subtract(3, 'month').startOf('month');

  useEffect(() => {
    fetchLocationsBySpoc();
  }, []);

  // Fetch devices whenever locations or device type changes
  useEffect(() => {
    if (selectedLocations.length > 0) {
      fetchAllDeviceIds();
    } else {
      setAllDeviceIds([]);
    }
  }, [selectedLocations, deviceType]);

  useEffect(() => {
    if (isCustomModalVisible) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [isCustomModalVisible]);

  const fetchLocationsBySpoc = () => {
    const authToken = JSON.parse(localStorage.getItem('headData'));
    axios
      .get(`${process.env.REACT_APP_API_URL}basic-data/get-location-by-spoc`, {
        params: { spoc: UserObj?.rec_id, client: UserObj?.company_id },
        headers: {
          Authorization: authToken?.spintlyToken,
          'Content-Type': 'application/json',
        },
      })
      .then(res => {
        if (res.data.status === 'success') {
          const formattedLocations = res?.data?.data?.map(location => ({
            label: location.location_name,
            value: location.rec_id,
          }));
          setLocations(formattedLocations);
        }
      })
      .catch(err => console.log('Error fetching locations:', err));
  };

  const fetchAllDeviceIds = async () => {
    if (selectedLocations.length === 0) {
      setAllDeviceIds([]);
      return;
    }

    setFetchingDevices(true);
    try {
      const selectedLocationIds = selectedLocations.map(loc => loc.value).join(',');

      const res = await axiosInstance.get(
        `${process.env.REACT_APP_API_GATEWAYS_URL}access-control/v3/api/device/get-device-data`,
        {
          params: {
            company_id: UserObj.company_id,
            location: selectedLocationIds,
            deviceFor: 'All',
          },
        }
      );

      if (res.data.statusValue === 200 && Array.isArray(res.data.data)) {
        const deviceIds = res.data.data.map(device => device._id);
        setAllDeviceIds(deviceIds);
      } else {
        setAllDeviceIds([]);
      }
    } catch (error) {
      console.error('Error fetching device IDs:', error);
      setAllDeviceIds([]);
      toast.error('Failed to load devices for selected locations.');
    } finally {
      setFetchingDevices(false);
    }
  };

  const handleInputChange = (field, value) => {
    const dateValue = value ? dayjs(value) : null;
    if (field === 'startDate') {
      setFilters(prev => {
        // Reset end date if it's not in the same month/year as the new start date
        const shouldResetEnd =
          prev.endDate &&
          dateValue &&
          (prev.endDate.isBefore(dateValue, 'day') ||
            prev.endDate.month() !== dateValue.month() ||
            prev.endDate.year() !== dateValue.year());
        return {
          ...prev,
          startDate: dateValue,
          endDate: shouldResetEnd ? null : prev.endDate,
        };
      });
    } else {
      setFilters(prev => ({ ...prev, endDate: dateValue }));
    }
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validateForm = (startDate, endDate, locations) => {
    const validationErrors = {};

    if (locations.length === 0) {
      validationErrors.location = 'Please select at least one location';
    }
    if (!startDate) {
      validationErrors.startDate = 'Please choose start date';
    }
    if (!endDate) {
      validationErrors.endDate = 'Please choose end date';
    } else if (startDate && endDate.isBefore(startDate, 'day')) {
      validationErrors.endDate = 'End date must be on or after start date';
    } else if (
      !isCustomModalVisible &&
      startDate &&
      endDate &&
      (startDate.month() !== endDate.month() || startDate.year() !== endDate.year())
    ) {
      validationErrors.endDate = `Date range must be within a single calendar month. Please select dates within ${startDate.format('MMMM YYYY')} only.`;
    }

    return validationErrors;
  };

  const resetForm = () => {
    setSelectedLocations([]);
    setFilters({ startDate: null, endDate: null });
    setAllDeviceIds([]);
    setErrors({});
    setDeviceType(['All']);
    setCustomDate({
      startDate: null,
      endDate: null,
      selectedLocations: [],
      deviceType: ['All'],
    });
    setCustomErrors({});
  };

  const handleAccessLogsEmailSent = async () => {
    const validation = validateForm(filters.startDate, filters.endDate, selectedLocations);

    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }

    // Optional: warn if no devices found
    if (allDeviceIds.length === 0) {
      toast.warning('No devices found for the selected locations and device type. Export may be empty.');
    }

    setLoadingExport(true);

    try {
      const selectedLocationIds = selectedLocations.map(loc => loc.value);

      const queryParams = new URLSearchParams({
        location_id: JSON.stringify(selectedLocationIds),
        device_id: JSON.stringify(allDeviceIds), // All devices
        to_date: filters.endDate.format('YYYY-MM-DD'),
        from_date: filters.startDate.format('YYYY-MM-DD'),
        deviceType: JSON.stringify(deviceType),
        company_id: UserObj?.company_id,
        email: JSON.stringify(UserObj?.email),
      }).toString();

      const res = await axiosInstance.post(
        `${process.env.REACT_APP_API_GATEWAYS_URL}reports/access-logs/getReport?${queryParams}`
      );

      if (res.status === 200) {
        toast.success('Access Log data will be sent to your inbox shortly');
        resetForm();
        onClose();
      } else {
        toast.error('Something went wrong while exporting logs.');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export access logs.');
    } finally {
      setLoadingExport(false);
    }
  };

  const handleExportExceed = async (e, isCustom = false) => {
    e.preventDefault();

    const startDate = isCustom ? customDate.startDate : filters.startDate;
    const endDate = isCustom ? customDate.endDate : filters.endDate;
    const locations = isCustom ? customDate.selectedLocations : selectedLocations;
    const currentDeviceType = isCustom ? customDate.deviceType : deviceType;

    const validation = validateForm(startDate, endDate, locations);

    if (Object.keys(validation).length > 0) {
      isCustom ? setCustomErrors(validation) : setErrors(validation);
      return;
    }

    let deviceIdsToSend = allDeviceIds;

    // For custom export, fetch devices based on custom selections
    if (isCustom) {
      try {
        const locIds = locations.map(loc => loc.value).join(',');
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_GATEWAYS_URL}access-control/v3/api/device/get-device-data`,
          {
            params: {
              company_id: UserObj.company_id,
              location: locIds,

            },
          }
        );

        deviceIdsToSend = res.data.statusValue === 200 && Array.isArray(res.data.data)
          ? res.data.data.map(d => d._id)
          : [];
      } catch (err) {
        toast.error('Failed to fetch devices for custom export.');
        setLoadingExport(false);
        return;
      }
    }

    setLoadingExport(true);

    try {
      const locationIds = locations.map(loc => loc.value);

      if (isCustom) {
        const payload = {
          from_date: startDate.format('YYYY-MM-DD'),
          to_date: endDate.format('YYYY-MM-DD'),
          reportName: 'Access_Logs',
          location_id: JSON.stringify(locationIds.map(id => id.toString())),
          device_id: JSON.stringify(deviceIdsToSend),
          deviceType: JSON.stringify(currentDeviceType),
          email: UserObj?.email,
          company_id: UserObj?.company_id.toString(),
        };

        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_GATEWAYS_URL}cspoc/api/v2/basic-data/request-report-to-download`,
          payload
        );

        if (response.status === 200) {
          toast.success('Custom access logs export request sent. Please check your inbox.');
          resetForm();
          setIsCustomModalVisible(false);
          onClose();
        }
      } else {
        await handleAccessLogsEmailSent();
      }
    } catch (error) {
      console.error('Error exporting access logs:', error);
      toast.error('Export failed. Please try again later.');
    } finally {
      setLoadingExport(false);
    }
  };

  const disabledStartDate = current => {
    return current && (current < threeMonthsAgoStart || current > today.endOf('day'));
  };

  const disabledEndDate = current => {
    if (!current) return false;
    // Must be within the 90-day window and not in the future
    if (current < threeMonthsAgoStart || current > today.endOf('day')) return true;
    if (filters.startDate) {
      // Must be on or after start date
      if (current.isBefore(filters.startDate, 'day')) return true;
      // Must be in the same calendar month as start date
      if (current.month() !== filters.startDate.month() || current.year() !== filters.startDate.year()) return true;
    }
    return false;
  };
  const deviceTypeOptions = [
    { value: "All", label: "All" },
    { value: "Face", label: "Face" },
    { value: "QR", label: "QR" },
  ];

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <SwOffCanvas
        show={show}
        onClose={handleClose}
        title="Export Access Logs"
        placement="end"
        size="sm"
        backdrop="static"
        keyboard={false}
        footer={
          <div className="OffCanvasFooter_Btns">
            <SwButton
              variant="dark-outline"
              onClick={resetForm}
            >
              Reset
            </SwButton>
            <SwButton
              variant="primary"
              onClick={e => handleExportExceed(e, false)}
              className="ml10"
              disabled={loadingExport || fetchingDevices}
            >
              {loadingExport ? 'Exporting...' : 'Export'}
            </SwButton>
          </div>
        }
      >
        {/* Location */}
        <div className="SwFormGroup">
          <label className="SwFormLabel">Location Name<span className='Required'>*</span></label>
          <SwMultiSelect
            options={locations}
            value={selectedLocations}
            onChange={setSelectedLocations}
            labelledBy="Select"
            placeholder="Select"
          />
          <span className="SwTextDanger SwTextSmall">{errors.location}</span>
        </div>

        {/* Start Date */}
        <div className="SwFormGroup">
          <label className="SwFormLabel">Start Date<span className='Required'>*</span></label>
          <AntDDatePicker
            format="YYYY-MM-DD"
            value={filters.startDate}
            onChange={date => handleInputChange('startDate', date)}
            style={{ width: '100%' }}
            disabledDate={disabledStartDate}
            placeholder="Select Start Date"
          />
          <span className="SwTextDanger SwTextSmall">{errors.startDate}</span>
        </div>

        {/* End Date */}
        <div className="SwFormGroup">
          <label className="SwFormLabel">End Date<span className='Required'>*</span></label>
          <AntDDatePicker
            format="YYYY-MM-DD"
            value={filters.endDate}
            onChange={date => handleInputChange('endDate', date)}
            style={{ width: '100%' }}
            disabledDate={disabledEndDate}
            placeholder="Select End Date"
          />
          <span className="SwTextDanger SwTextSmall">{errors.endDate}</span>
        </div>

        {/* Device Type */}
        <div className="SwFormGroup">
          <label className='SwFormLabel'>Device Type</label>
          <SwSelect
            options={deviceTypeOptions}
            isClearable={false}
            placeholder='Select Device type'
            value={deviceTypeOptions.find(
              opt => opt.value === (deviceType[0] || "All")
            )}
            onChange={(selectedOption) => {
              setDeviceType([selectedOption ? selectedOption.value : "All"]);
            }}
          />
        </div>
      </SwOffCanvas>
    </>
  );
};

export default AccessLogsExport;